import { useCallback, useState } from 'react';
import {
  useCreateAutomationAndSendConditionsMutation,
  WorkflowEventSettingCreateInput,
  AutomationEditorAutomationFragment,
  ConditionCreateWithoutSetInput,
  CreateAutomationAndSendConditionsMutation,
  CreateAutomationAndSendConditionsMutationVariables,
  GetClinicAutomationsDocument,
} from 'shared/types/graphql';
import { MutationHookOptions } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { getAutomationsListQuery, getGlobalAutomationsListQuery } from '../constants/operationNames';
import { useAppSelector } from 'state/hooks';

interface CreateAutomationOptions {
  workflowEventSettingData: WorkflowEventSettingCreateInput;
  conditionsData?: ConditionCreateWithoutSetInput[];
}

export const useCreateAutomation = (
  options?: Pick<
    MutationHookOptions<CreateAutomationAndSendConditionsMutation, CreateAutomationAndSendConditionsMutationVariables>,
    'onCompleted'
  >,
): {
  createAutomation: (options: CreateAutomationOptions) => Promise<AutomationEditorAutomationFragment | undefined>;
  isLoading: boolean;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);

  const [createAutomationAndSendConditions] = useCreateAutomationAndSendConditionsMutation({
    ...options,
    awaitRefetchQueries: true,
    refetchQueries: isGlobalEditor
      ? [getGlobalAutomationsListQuery]
      : [getAutomationsListQuery, GetClinicAutomationsDocument],
  });

  const createAutomation = useCallback(
    async ({ workflowEventSettingData, conditionsData }: CreateAutomationOptions) => {
      setIsLoading(true);
      const result = await createAutomationAndSendConditions({
        variables: {
          workflowEventSettingData,
          conditionsData: conditionsData || [],
        },
      });
      setIsLoading(false);
      const workflowEventSetting = result.data?.createWorkflowEventSettingAndConditions;
      if (!workflowEventSetting?.id) {
        console.log('Failed to create WorkflowEventSetting with options: ', options);
        Sentry.captureMessage('Failed to create WorkflowEventSetting', { extra: { options } });
        throw new Error('Failed to create WorkflowEventSetting');
      }
      return result.data?.createWorkflowEventSettingAndConditions;
    },
    [createAutomationAndSendConditions, options],
  );

  return { createAutomation, isLoading };
};
